<script setup lang="ts">
import { computed, inject, PropType, ref, toRefs, watchEffect } from "vue";
import {
	generateColumns,
	transformDataForPivot,
} from "@/modules/explorer/components/Metrics/MetricHelper";
import { documentKey } from "@/modules/explorer/ExplorerUtils";
import { t } from "@/plugins/i18n";

const isDialogVisible = defineModel<boolean>();
const emit = defineEmits(["update"]);
const props = defineProps({
	dataTableToEdit: {
		type: Object as PropType<App.Data.Explorer.DataTableDO>,
		require: false,
	},
	data: {
		type: Array as PropType<Record<string, string>[]>,
		default: () => [],
	},
});

const { dataTableToEdit, data } = toRefs(props);

const document = inject(documentKey);

const comparisonPeriods = computed(() => {
	return [
		document.value.date_comparison.comparisonLabel || "Y",
		document.value.date_comparison.isBudgetComparisonActive ? "Bud" : null,
		...document.value.date_comparison.periods.map((period) => `Y${period}`),
		...document.value.date_comparison.customPeriods.map(
			(period) => period.name
		),
	]
		.filter(Boolean)
		.sort((a, b) => {
			if (a === "Bud") return 1;
			if (b === "Bud") return -1;
			return a.localeCompare(b, undefined, { numeric: true });
		});
});

const pivotResult = computed(() => {
	if (!dataTableToEdit.value.pivot_dimension) {
		return { pivotedData: data.value, uniquePivotValues: [] };
	}
	return transformDataForPivot(data.value, dataTableToEdit.value);
});

const columns = computed(() =>
	generateColumns(
		dataTableToEdit.value,
		dataTableToEdit.value.metrics,
		comparisonPeriods.value,
		document.value.date_comparison.isBudgetComparisonActive,
		false,
		true,
		pivotResult.value.uniquePivotValues
	)
);

const isLowLevelColumns = ref(false);

function normalizeHeader(header: string): string {
	switch (header) {
		case "Act":
			return "Actual";
		case "Pred":
			return "Predicted";
		case "ΔAbs":
			return "Δ Absolute";
		case "ΔRel":
			return "Δ Relative";
		default:
			return header;
	}
}

const flattenedColumns = computed(() => {
	const out: Array<{
		header: string;
		fullHeader: string;
		accessorKey: string;
		metricIndex: number;
	}> = [];

	function walk(cols: any[], metricIndex: number, parentHeader = "") {
		for (const col of cols) {
			const fullHeader = parentHeader
				? `${parentHeader} (${normalizeHeader(col.header)})`
				: col.header;

			if (col.columns && col.columns.length > 0) {
				walk(col.columns, metricIndex, fullHeader);
			} else if (col.accessorKey) {
				out.push({
					header: col.header,
					fullHeader,
					accessorKey: col.accessorKey,
					metricIndex,
				});
			}
		}
	}

	columns.value.forEach((metric, mIdx) => {
		if (metric.columns && metric.columns.length > 0) {
			walk(metric.columns, mIdx, metric.header);
		} else if (metric.accessorKey) {
			out.push({
				header: metric.header,
				fullHeader: metric.header,
				accessorKey: metric.accessorKey,
				metricIndex: mIdx,
			});
		}
	});

	return out;
});

watchEffect(() => {
	// I use this ref to check if the user has either year-over-year or split_prediction enabled
	// If isLowLevelColumns.value is false this means that both are disabled.
	function hasNestedColumns(cols: any[]): boolean {
		return cols.some(
			(col) =>
				(Array.isArray(col.columns) && col.columns.length > 0) ||
				(col.columns && hasNestedColumns(col.columns))
		);
	}

	isLowLevelColumns.value = !hasNestedColumns(columns.value);
});

const hasOnlyHiddenColumns = computed(() => {
	if (isLowLevelColumns.value) {
		return dataTableToEdit.value?.metrics.every((metric) =>
			dataTableToEdit.value.hidden_columns.includes(metric.name_clean)
		);
	}

	const allColumnKeys = flattenedColumns.value.map((c) => c.accessorKey);
	const hiddenColumns = dataTableToEdit.value?.hidden_columns ?? [];

	return (
		allColumnKeys.length === hiddenColumns.length &&
		allColumnKeys.every((v) => hiddenColumns.includes(v)) &&
		hiddenColumns.every((v) => allColumnKeys.includes(v))
	);
});

function isColumnVisible(metricIndex: number, accessorKey: string) {
	return !dataTableToEdit.value?.hidden_columns?.includes(accessorKey);
}

function isMetricVisible(metricNameClean: string, metricIndex: number) {
	const dataTable = dataTableToEdit.value;
	if (!dataTable) return true;

	const hidden = dataTable.hidden_columns || [];

	const relatedColumns = flattenedColumns.value
		.filter((col) => col.metricIndex === metricIndex)
		.map((col) => col.accessorKey);

	const metricHidden = hidden.includes(metricNameClean);

	const allColsHidden =
		relatedColumns.length > 0 &&
		relatedColumns.every((col) => hidden.includes(col));

	return !(metricHidden || allColsHidden);
}

function isMetricIndeterminate(metricIndex: number) {
	const hiddenColumns = dataTableToEdit.value?.hidden_columns;
	if (!hiddenColumns) return false;
	const allCols = flattenedColumns.value.filter(
		(c) => c.metricIndex === metricIndex
	);

	if (allCols.length === 0) return false;
	const hiddenCount = allCols.filter((c) =>
		hiddenColumns.includes(c.accessorKey)
	).length;

	return hiddenCount > 0 && hiddenCount < allCols.length;
}

function toggleMetric(
	metricNameClean: string,
	metricIndex: number,
	checked: boolean
) {
	const dataTable = dataTableToEdit.value;
	if (!dataTable) return;

	let hidden = dataTable.hidden_columns || [];

	const relatedColumns = flattenedColumns.value
		.filter((col) => col.metricIndex === metricIndex)
		.map((col) => col.accessorKey);

	if (checked) {
		hidden = hidden.filter((c) => c !== metricNameClean);
		for (const column of relatedColumns) {
			hidden = hidden.filter((c) => c !== column);
		}
	} else {
		if (!hidden.includes(metricNameClean)) hidden.push(metricNameClean);
		for (const column of relatedColumns) {
			if (!hidden.includes(column)) {
				hidden.push(column);
			}
		}
	}

	dataTable.hidden_columns = [...hidden];
}

function toggleItem(accessorKey: string, checked: boolean, nameClean: string) {
	const dataTable = dataTableToEdit.value;
	if (!dataTable) return;

	const hidden = dataTable.hidden_columns || [];
	const exists = hidden.includes(accessorKey);

	if (checked && exists) {
		dataTable.hidden_columns = hidden.filter(
			(c) => c !== accessorKey && c !== nameClean
		);
	} else if (!checked && !exists) {
		dataTable.hidden_columns = [...hidden, accessorKey];
	}
}

function isWholeMetricHidden(metricNameClean: string) {
	const dataTable = dataTableToEdit.value;
	return dataTable?.hidden_columns?.includes(metricNameClean);
}

function toggleWholeMetric(metricNameClean: string, checked: boolean) {
	const dataTable = dataTableToEdit.value;
	if (!dataTable) return;

	if (!Array.isArray(dataTable.hidden_columns)) {
		dataTable.hidden_columns = [];
	}

	const hidden = dataTable.hidden_columns;
	const index = hidden.indexOf(metricNameClean);

	if (checked && index !== -1) {
		hidden.splice(index, 1);
	} else if (!checked && index === -1) {
		hidden.push(metricNameClean);
	}
}

const closeDialog = () => {
	emit("update", dataTableToEdit.value);
	isDialogVisible.value = false;
};
</script>

<template>
	<v-dialog
		v-model="isDialogVisible"
		scrim="#000000"
		overlay-color="black"
		max-width="600"
		scrollable
	>
		<div class="dialog-content overflow-y-auto">
			<v-card class="grid-config">
				<v-toolbar color="background">
					<v-toolbar-title>Hide Columns</v-toolbar-title>
					<v-spacer></v-spacer>
				</v-toolbar>
				<v-divider />
				<div class="flex-grow-1 hidden_columns_selection ma-0">
					<v-expansion-panels v-if="!isLowLevelColumns" multiple>
						<v-expansion-panel
							v-for="(metric, index) in dataTableToEdit.metrics"
							:key="metric.name_clean"
							class="ma-0 pa-0"
						>
							<v-expansion-panel-title color="white">
								<v-checkbox
									:label="t(`properties.${metric.name_clean}.title`)"
									:model-value="isMetricVisible(metric.name_clean, index)"
									:indeterminate="isMetricIndeterminate(index)"
									@update:model-value="
										(checked) => toggleMetric(metric.name_clean, index, checked)
									"
									color="primary"
									class="ma-0 pa-0"
									@click.stop
								/>
							</v-expansion-panel-title>

							<v-expansion-panel-text>
								<v-checkbox
									v-for="col in flattenedColumns.filter(
										(c) => c.metricIndex === index
									)"
									:key="col.accessorKey"
									:label="col.fullHeader"
									:model-value="
										isColumnVisible(col.metricIndex, col.accessorKey)
									"
									@update:model-value="
										(checked) =>
											toggleItem(col.accessorKey, checked, metric.name_clean)
									"
									density="compact"
									color="primary"
									class="ml-4"
								/>
							</v-expansion-panel-text>
						</v-expansion-panel>
					</v-expansion-panels>
					<div v-else class="flex-grow-1 hidden_columns_selection mt-4 ml-4">
						<div>
							<v-checkbox
								v-for="metric in dataTableToEdit.metrics"
								:key="metric.name_clean"
								:label="t(`properties.${metric.name_clean}.title`)"
								:model-value="!isWholeMetricHidden(metric.name_clean)"
								@update:model-value="
									(checked) => toggleWholeMetric(metric.name_clean, checked)
								"
								density="compact"
								color="primary"
							/>
						</div>
					</div>
				</div>

				<v-alert
					class="ma-4 mt-0"
					v-if="hasOnlyHiddenColumns"
					title="Invalid Configuration"
					:text="'Please select at least one column'"
					variant="tonal"
					type="error"
				/>
				<!-- Bottom Actions -->
				<div>
					<v-divider style="border-color: unset !important" />
					<v-card-actions>
						<v-spacer></v-spacer>
						<v-btn
							color="primary"
							variant="text"
							@click="isDialogVisible = false"
							>Close</v-btn
						>
						<v-btn
							:disabled="hasOnlyHiddenColumns"
							color="primary"
							variant="flat"
							@click="closeDialog"
							>Save</v-btn
						>
					</v-card-actions>
				</div>
			</v-card>
		</div>
	</v-dialog>
</template>

<style scoped lang="scss">
.grid-config {
	display: grid !important;
	grid-template-columns: 1fr;
	grid-template-rows: min-content 1fr auto auto;
	height: 100%;
}

.hidden_columns_selection {
	overflow-y: auto;
}

.dialog-content {
	display: grid;
	margin: auto;
	gap: 8px;
	grid-template-columns: 600px 400px;
	@media (max-width: 756px) {
		grid-template-columns: 1fr;
	}
}
</style>
